import type { SportEvent } from '../../types/SportEventTypes';
import { readPayload } from '../shared';

/**
 * Points scored by a single NBA play-by-play event.
 *
 * Only *successful* scoring plays carry a positive value; misses, fouls,
 * rebounds, substitutions, etc. score 0. Keyed off `payload.type` — the
 * parser already folds make/miss and shot kind into that discriminator
 * (see ./parser.ts), so we never have to re-read `data.success`.
 *
 * Free throws score 1, made 2-pointers score 2, made 3-pointers score 3.
 * Misses / fouls / rebounds / subs aren't keys here, so they score 0.
 */
const POINTS_BY_EVENT_TYPE: Readonly<Record<string, number>> = {
  free_throw_made: 1,
  basket_made_2pt: 2,
  basket_made_3pt: 3,
};

/**
 * How many points this event put on the board. Returns 0 for any event that
 * isn't a successful scoring play (including missed free throws).
 */
export function pointsForNbaEvent(event: SportEvent): number {
  const { type } = readPayload(event);
  if (!type) return 0;
  return POINTS_BY_EVENT_TYPE[type] ?? 0;
}
